/**
 * Fix for Chrome translation extension DOM manipulation errors
 *
 * This utility overrides native DOM methods to gracefully handle errors
 * caused by translation extensions that modify the DOM structure.
 *
 * Translation extensions like Google Translate wrap text nodes in additional
 * elements (e.g., <font> tags), which can cause React's virtual DOM to become
 * out of sync with the actual DOM, leading to:
 * - "Failed to execute 'insertBefore' on 'Node'"
 * - "Failed to execute 'removeChild' on 'Node'"
 *
 * This fix catches these errors and handles them gracefully without crashing the app.
 */

/**
 * Helper function to safely attempt DOM operations with fallbacks
 */
function safeDomOperation<T>(operation: () => T, fallback: () => T, operationName: string, context: string): T {
    try {
        return operation();
    } catch (error: unknown) {
        if (error instanceof Error && error.name === 'NotFoundError') {
            console.warn(
                `Translation extension interference detected in ${operationName} (${context}), using fallback`
            );
            try {
                return fallback();
            } catch (fallbackError) {
                console.error(`Fallback operation also failed for ${operationName} (${context}):`, fallbackError);
                throw fallbackError;
            }
        }
        throw error;
    }
}

export function applyTranslationExtensionFix(): void {
    if (typeof window === 'undefined' || (window as { __translationFixApplied?: boolean }).__translationFixApplied) {
        return;
    }

    // Override removeChild to handle NotFoundError gracefully
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function <T extends Node>(child: T): T {
        return safeDomOperation(
            () => originalRemoveChild.call(this, child) as T,
            () => {
                // The child node was already removed by translation extension
                // Check if it's still in the DOM to avoid false positives
                if (child.parentNode === null) {
                    console.warn('Translation extension removed node before React could:', child);
                    return child;
                }
                // If the child is still in the DOM but not a child of this node,
                // it might have been moved by the translation extension
                console.warn('Node was moved by translation extension, attempting to remove from current parent');
                if (child.parentNode) {
                    return originalRemoveChild.call(child.parentNode, child) as T;
                }
                return child;
            },
            'removeChild',
            `parent: ${this.nodeName}, child: ${child.nodeName}`
        );
    };

    // Override insertBefore to handle NotFoundError gracefully
    const originalInsertBefore = Node.prototype.insertBefore;
    Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
        return safeDomOperation(
            () => originalInsertBefore.call(this, newNode, referenceNode) as T,
            () => {
                // The reference node was modified by translation extension
                // Try to find a suitable alternative reference node or append to end
                console.warn(
                    'Translation extension modified reference node, attempting to find alternative insertion point:',
                    referenceNode
                );

                // If referenceNode is null, append to end (this is the expected behavior)
                if (referenceNode === null) {
                    return this.appendChild(newNode) as T;
                }

                // Try to find the reference node's parent and insert after it
                const parent = referenceNode.parentNode;
                if (parent && parent !== this) {
                    try {
                        return originalInsertBefore.call(parent, newNode, referenceNode.nextSibling) as T;
                    } catch {
                        // If that fails, append to the parent
                        return parent.appendChild(newNode) as T;
                    }
                }

                // Last resort: append to this node
                return this.appendChild(newNode) as T;
            },
            'insertBefore',
            `parent: ${this.nodeName}, newNode: ${newNode.nodeName}, referenceNode: ${referenceNode?.nodeName || 'null'}`
        );
    };

    // Override appendChild to handle NotFoundError gracefully
    const originalAppendChild = Node.prototype.appendChild;
    Node.prototype.appendChild = function <T extends Node>(child: T): T {
        return safeDomOperation(
            () => originalAppendChild.call(this, child) as T,
            () => {
                // The parent node was modified by translation extension
                console.warn(
                    'Translation extension modified parent node during appendChild, attempting recovery:',
                    this
                );

                // Check if the child is already in the DOM somewhere else
                if (child.parentNode) {
                    // Child is already attached to another parent, just return it
                    console.warn('Child node is already attached to another parent, returning as-is');
                    return child;
                }

                // Try to find the parent's parent and append there
                const grandParent = this.parentNode;
                if (grandParent) {
                    try {
                        console.warn('Attempting to append to grandparent node');
                        return originalAppendChild.call(grandParent, child) as T;
                    } catch {
                        console.warn('Failed to append to grandparent, child will be orphaned');
                        // If we can't append anywhere, we need to return the child
                        // but this creates a DOM inconsistency - log it for debugging
                        return child;
                    }
                }

                // If no parent is available, we can't append the child
                // This is a serious DOM inconsistency - log it and return the child
                console.error('Cannot append child node - no valid parent found. DOM may be inconsistent.');
                return child;
            },
            'appendChild',
            `parent: ${this.nodeName}, child: ${child.nodeName}`
        );
    };

    // Mark as applied to prevent multiple applications
    (window as { __translationFixApplied?: boolean }).__translationFixApplied = true;

    console.log('Translation extension fix applied successfully');
}
